import React from "react";
import type { ReactNode } from "react";
import AdminDashboard from "../pages/AdminDashboard";
import TeamsPage from "../pages/TeamsPage";
import TeamDetailPage from "../pages/TeamDetailPage";
import PlayersPage from "../pages/PlayersPage";
import PlayerDetailPage from "../pages/PlayerDetailPage";
import ViolationsPage from "../pages/ViolationsPage";
import OrganizationsPage from "../pages/OrganizationsPage";
import ProgramsPage from "../pages/ProgramsPage";
import LeaguesPage from "../pages/LeaguesPage";
import DraftsPage from "../pages/DraftsPage";
import EvaluationsPage from "../pages/EvaluationsPage";
import PitchLogsPage from "../pages/PitchLogsPage";
import UserManagementPage from "../pages/UserManagementPage";
import RulesPage from "../pages/RulesPage";
import SeasonsPage from "../pages/SeasonsPage";
import SchedulePage from "../pages/SchedulePage";
import FaqPage from "../pages/FaqPage";
import CoachLineupPlannerPage from "../pages/CoachLineupPlannerPage";
import type { ActiveRoleScope } from "../lib/roleContext";

export type AdminRouteDef = {
  path: string;
  allowedRoles: string[];
  render: (userRoles: string[], activeRoleScope?: ActiveRoleScope | null) => ReactNode;
};

const admins = ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"];
const staff = [...admins, "Coach"];
const pipeline = [...staff, "Evaluator"];
const family = [...staff, "Parent", "Parents"];

// This is the migration seam for the in-place rebuild.
// Existing pages stay live while route ownership moves into feature modules.
export const adminRouteCatalog: AdminRouteDef[] = [
  {
    path: "dashboard",
    allowedRoles: [],
    render: (userRoles, activeRoleScope) => <AdminDashboard userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "organizations",
    allowedRoles: admins,
    render: (userRoles) => <OrganizationsPage userRoles={userRoles} />
  },
  {
    path: "programs",
    allowedRoles: admins,
    render: (userRoles) => <ProgramsPage userRoles={userRoles} />
  },
  {
    path: "leagues",
    allowedRoles: admins,
    render: (userRoles) => <LeaguesPage userRoles={userRoles} />
  },
  {
    path: "drafts",
    allowedRoles: admins,
    render: (userRoles) => <DraftsPage userRoles={userRoles} />
  },
  {
    path: "evaluations",
    allowedRoles: pipeline,
    render: (userRoles) => <EvaluationsPage userRoles={userRoles} />
  },
  {
    path: "teams",
    allowedRoles: staff,
    render: (userRoles, activeRoleScope) => <TeamsPage userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "teams/:teamId",
    allowedRoles: staff,
    render: (userRoles, activeRoleScope) => <TeamDetailPage userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "teams/:teamId/lineups",
    allowedRoles: staff,
    render: (userRoles) => <CoachLineupPlannerPage userRoles={userRoles} />
  },
  {
    path: "players",
    allowedRoles: family,
    render: (userRoles, activeRoleScope) => <PlayersPage userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "players/:playerId",
    allowedRoles: family,
    render: (userRoles) => <PlayerDetailPage userRoles={userRoles} />
  },
  {
    path: "pitch-logs",
    allowedRoles: [],
    render: (userRoles, activeRoleScope) => <PitchLogsPage userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "rules",
    allowedRoles: admins,
    render: (userRoles) => <RulesPage userRoles={userRoles} />
  },
  {
    path: "violations",
    allowedRoles: family,
    render: (userRoles, activeRoleScope) => <ViolationsPage userRoles={userRoles} activeRoleScope={activeRoleScope} />
  },
  {
    path: "schedule",
    allowedRoles: staff,
    render: (userRoles) => <SchedulePage userRoles={userRoles} />
  },
  {
    path: "fields",
    allowedRoles: staff,
    render: (userRoles) => <SchedulePage userRoles={userRoles} />
  },
  {
    path: "seasons",
    allowedRoles: admins,
    render: (userRoles) => <SeasonsPage userRoles={userRoles} />
  },
  {
    path: "users",
    allowedRoles: admins,
    render: () => <UserManagementPage />
  },
  {
    path: "help",
    allowedRoles: [],
    render: () => <FaqPage />
  }
];
